import { useT } from "../../../../shared/i18n/i18n";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Copy,
  Globe2,
  History,
  Loader2,
  LockKeyhole,
  Search,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { ResourceAccess, ResourceAccessExplanation, ResourceScope } from "../../../../entities/access/model/use-workspace-access";
import { useTRPC } from "../../../../shared/api/trpc";

type AccessLevel = "viewer" | "commenter" | "editor" | "manager";
type AccessMode = "inherited" | "workspace" | "private";
type Principal = { id: string; type: "user" | "team" };
type Grant = { accessLevel: AccessLevel; principal: Principal };

// Screen text, so it is built with the reader's translator rather than frozen
// at module load.
const accessLevelKeys: Record<AccessLevel, string> = {
  viewer: "access.view",
  commenter: "access.comment",
  editor: "common.edit",
  manager: "access.edit",
};

const accessModes: Array<{
  descriptionKey: string;
  icon: typeof Globe2;
  labelKey: string;
  value: AccessMode;
}> = [
  { value: "inherited", labelKey: "access.inherit", descriptionKey: "access.inheritHint", icon: UsersRound },
  { value: "workspace", labelKey: "access.everyone", descriptionKey: "access.everyoneHint", icon: Globe2 },
  { value: "private", labelKey: "access.private", descriptionKey: "access.privateHint", icon: LockKeyhole },
];

export function ResourceAccessDialog({ name, onOpenChange, open, scope }: {
  name: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  scope: ResourceScope;
}) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const accessQuery = useQuery(trpc.access.scope.queryOptions(scope, { enabled: open }));
  const updateMutation = useMutation(trpc.access.updateScope.mutationOptions());
  const [mode, setMode] = useState<AccessMode>("inherited");
  const [grants, setGrants] = useState<Grant[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showInherited, setShowInherited] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [inspectedUserId, setInspectedUserId] = useState("");
  const explanationQuery = useQuery(trpc.access.explainScope.queryOptions(
    inspectedUserId ? { scope, userId: inspectedUserId } : skipToken,
  ));
  const data: ResourceAccess | null = accessQuery.data ?? null;
  const explanation: ResourceAccessExplanation | null = explanationQuery.data ?? null;
  const isSaving = updateMutation.isPending;
  const isExplaining = explanationQuery.isPending;
  const visibleError = error ?? (accessQuery.error instanceof Error ? accessQuery.error.message : accessQuery.error ? String(accessQuery.error) : explanationQuery.error instanceof Error ? explanationQuery.error.message : explanationQuery.error ? String(explanationQuery.error) : null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setQuery("");
    setCopied(false);
    setShowInherited(false);
    setShowDetails(false);
    setInspectedUserId("");
  }, [open, scope.id, scope.type]);

  useEffect(() => {
    if (!open || !data) return;
    setMode(data.path.length === 1 && data.state.mode === "inherited" ? "workspace" : data.state.mode);
    setGrants(data.state.grants.filter((grant) => !(grant.principal.type === "user" && grant.principal.id === data.creatorId)));
  }, [data, open]);

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const directKeys = useMemo(() => new Set([
    ...grants.map((grant) => principalKey(grant.principal)),
    ...(data?.creatorId ? [`user:${data.creatorId}`] : []),
  ]), [data?.creatorId, grants]);
  const candidates = useMemo(() => {
    if (!data) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return [
      ...data.teams.map((team) => ({ id: team.id, name: team.name, detail: `${team.memberIds.length} members`, type: "team" as const })),
      ...data.members.map((member) => ({ id: member.userId, name: member.displayName ?? member.email, detail: member.email, type: "user" as const })),
    ].filter((item) => !directKeys.has(`${item.type}:${item.id}`))
      .filter((item) => !normalizedQuery || `${item.name} ${item.detail}`.toLowerCase().includes(normalizedQuery));
  }, [data, directKeys, query]);

  const inheritedGrants = data?.states.slice(0, -1).flatMap((state) =>
    state.grants.map((grant) => ({ ...grant, sourceName: state.scope.name ?? state.scope.type })),
  ) ?? [];

  const availableModes = accessModes.filter((option) => option.value !== "inherited" || (data?.path.length ?? 1) > 1);
  const activeMode = accessModes.find((option) => option.value === mode);
  const ActiveModeIcon = activeMode?.icon ?? Globe2;
  const activeModeLabel = activeMode ? t(activeMode.labelKey) : t("access.everyone");

  async function persist(next: { grants?: Grant[]; mode?: AccessMode }) {
    const nextGrants = next.grants ?? grants;
    const nextMode = next.mode ?? mode;
    setGrants(nextGrants);
    setMode(nextMode);
    setError(null);
    try {
      const result = await updateMutation.mutateAsync({ grants: nextGrants, mode: nextMode, scope });
      queryClient.setQueryData(trpc.access.scope.queryKey(scope), result);
      setGrants(result.state.grants.filter((grant) => !(grant.principal.type === "user" && grant.principal.id === result.creatorId)));
      setMode(result.path.length === 1 && result.state.mode === "inherited" ? "workspace" : result.state.mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update sharing settings.");
      // Revert optimistic change from the server state.
      await accessQuery.refetch();
    }
  }

  function addPrincipal(principal: Principal) {
    setQuery("");
    void persist({ grants: [...grants, { accessLevel: "editor", principal }] });
  }

  function updateGrant(principal: Principal, accessLevel: AccessLevel) {
    void persist({ grants: grants.map((grant) => principalKey(grant.principal) === principalKey(principal) ? { ...grant, accessLevel } : grant) });
  }

  function removeGrant(principal: Principal) {
    void persist({ grants: grants.filter((grant) => principalKey(grant.principal) !== principalKey(principal)) });
  }

  async function copyLink() {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
  }

  async function inspectAccess(userId: string) {
    setInspectedUserId(userId);
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex max-h-[min(88vh,42rem)] w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="p-5 pb-0 pr-12">
          <DialogTitle className="flex items-center gap-2 truncate text-base">
            Share “{name}”
            {isSaving ? <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" /> : null}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {visibleError ? <div className="px-5 pt-4"><Alert variant="destructive"><AlertDescription>{visibleError}</AlertDescription></Alert></div> : null}
          {!data && !visibleError ? <div className="grid min-h-52 place-items-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div> : null}

          {data ? (
            <div className="flex flex-col gap-5 p-5">
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("access.general")}</p>
                <div className="flex gap-2">
                  <Select onValueChange={(value) => void persist({ mode: value as AccessMode })} value={mode}>
                    <SelectTrigger className="min-w-0 flex-1" size="default">
                      <SelectValue>{() => <span className="flex items-center gap-2"><ActiveModeIcon className="size-4" /> {activeModeLabel}</span>}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="w-[min(24rem,var(--anchor-width))]">
                      {availableModes.map((option) => (
                        <SelectItem className="py-1.5 pr-8" key={option.value} value={option.value}>
                          <span className="flex items-center gap-2">
                            <option.icon className="size-4 shrink-0" />
                            <span className="flex flex-col">
                              <span className="text-sm leading-none">{t(option.labelKey)}</span>
                              <span className="whitespace-normal text-xs leading-snug text-muted-foreground">{t(option.descriptionKey)}</span>
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button aria-label={copied ? "Link copied" : t("access.copyPrivateLink")} onClick={() => void copyLink()} title={t("access.copyPrivateLink")} variant="outline">
                    {copied ? <Check className="text-emerald-500" /> : <Copy />} {copied ? t("board.copied") : t("shareLink.copy")}
                  </Button>
                </div>
                {mode === "private" ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">{t("access.creatorKeeps")}</p>
                ) : null}
              </div>

              <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    aria-expanded={Boolean(query.trim())}
                    aria-label={t("access.invite")}
                    className="pl-9"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("access.invitePlaceholder")}
                    role="combobox"
                    value={query}
                  />
                  {query.trim() ? (
                    <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-52 overflow-y-auto rounded-lg border bg-popover p-1 shadow-md">
                      {candidates.length > 0 ? candidates.map((candidate) => (
                        <button className="flex w-full items-center gap-2.5 rounded-md p-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30" key={`${candidate.type}:${candidate.id}`} onClick={() => addPrincipal({ id: candidate.id, type: candidate.type })} type="button">
                          <PrincipalIcon type={candidate.type} value={candidate.name} />
                          <span className="min-w-0 flex-1"><span className="block truncate text-sm">{candidate.name}</span><span className="block truncate text-xs text-muted-foreground">{candidate.detail}</span></span>
                          <span className="text-xs font-medium text-primary">{t("access.add")}</span>
                        </button>
                      )) : <p className="p-2 text-center text-xs text-muted-foreground">{t("access.noResults")}</p>}
                    </div>
                  ) : null}
                </div>

                <div className="mt-1 flex flex-col">
                  {data.creatorId ? (
                    <PrincipalRow
                      detail="Creator"
                      iconType="user"
                      name={principalName({ id: data.creatorId, type: "user" }, data)}
                      trailing={<span className="text-xs text-muted-foreground">{t("access.edit")}</span>}
                    />
                  ) : null}
                  {grants.map((grant) => (
                    <PrincipalRow
                      detail={grant.principal.type === "team" ? "Team" : principalDetail(grant.principal, data)}
                      iconType={grant.principal.type}
                      key={principalKey(grant.principal)}
                      name={principalName(grant.principal, data)}
                      trailing={(
                        <div className="flex items-center gap-1">
                          <AccessLevelSelect onChange={(level) => updateGrant(grant.principal, level)} value={grant.accessLevel} />
                          <Button aria-label={t("a11y.removeAccess", { name: principalName(grant.principal, data) })} onClick={() => removeGrant(grant.principal)} size="icon-sm" variant="ghost"><Trash2 /></Button>
                        </div>
                      )}
                    />
                  ))}
                </div>
              </div>

              {inheritedGrants.length > 0 ? (
                <Disclosure
                  onToggle={() => setShowInherited((value) => !value)}
                  open={showInherited}
                  title={t("access.inheritedCount", { count: inheritedGrants.length })}
                >
                  <p className="mb-2 text-xs text-muted-foreground">{t("access.readOnlyHere")}</p>
                  {inheritedGrants.map((grant, index) => (
                    <PrincipalRow
                      detail={`From ${grant.sourceName}`}
                      iconType={grant.principal.type}
                      key={`${principalKey(grant.principal)}:${grant.sourceName}:${index}`}
                      name={principalName(grant.principal, data)}
                      trailing={<span className="text-xs text-muted-foreground">{t(accessLevelKeys[grant.accessLevel])}</span>}
                    />
                  ))}
                </Disclosure>
              ) : null}

              <Disclosure
                onToggle={() => setShowDetails((value) => !value)}
                open={showDetails}
                title={t("access.details")}
              >
                <p className="mb-2 text-xs text-muted-foreground">{t("access.whyHasAccess")}</p>
                <Select onValueChange={(userId) => { if (userId) void inspectAccess(userId); }} value={inspectedUserId}>
                  <SelectTrigger aria-label={t("access.memberToInspect")} className="w-full" size="default">
                    <SelectValue>{inspectedUserId ? data.members.find((member) => member.userId === inspectedUserId)?.displayName ?? data.members.find((member) => member.userId === inspectedUserId)?.email : <span className="text-muted-foreground">{t("access.pickMember")}</span>}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>{data.members.map((member) => <SelectItem key={member.userId} value={member.userId}>{member.displayName ?? member.email}</SelectItem>)}</SelectContent>
                </Select>
                {isExplaining ? <div className="grid min-h-16 place-items-center"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div> : null}
                {explanation ? (
                  <div className="mt-2 rounded-lg border bg-muted/20 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2"><span className="text-xs font-medium">{t("access.effective")}</span><Badge variant={explanation.effectiveLevel ? "secondary" : "outline"}>{explanation.effectiveLevel ? t(accessLevelKeys[explanation.effectiveLevel]) : "No access"}</Badge></div>
                    <div className="grid gap-2">{explanation.reasons.map((reason, index) => (
                      <div className="flex items-start gap-2 text-xs" key={`${reason.kind}:${reason.scope?.id ?? "workspace"}:${index}`}>
                        <span className="mt-1 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
                        <span className="min-w-0 flex-1"><span className="block">{reason.label}</span>{reason.scope ? <span className="text-muted-foreground">{reason.scope.name ?? reason.scope.type}</span> : null}</span>
                        {reason.accessLevel ? <span className="text-muted-foreground">{t(accessLevelKeys[reason.accessLevel])}</span> : <span className="text-muted-foreground">reset</span>}
                      </div>
                    ))}</div>
                  </div>
                ) : null}

                {data.auditEvents.length > 0 ? (
                  <div className="mt-4">
                    <div className="mb-2 flex items-center gap-2"><History className="size-4 text-muted-foreground" /><span className="text-xs font-medium">{t("access.recentChanges")}</span></div>
                    <div className="grid gap-2.5">{data.auditEvents.slice(0, 5).map((event) => (
                      <div className="flex items-center gap-2.5" key={event.id}>
                        <PrincipalIcon type="user" value={event.actorDisplayName ?? event.actorEmail} />
                        <div className="min-w-0 flex-1"><p className="truncate text-xs"><span className="font-medium">{event.actorDisplayName ?? event.actorEmail}</span> updated sharing</p><p className="text-xs text-muted-foreground">{new Date(event.createdAt).toLocaleString()}</p></div>
                      </div>
                    ))}</div>
                  </div>
                ) : null}

                <div className="mt-4">
                  <p className="mb-1 text-xs font-medium">{t("access.path")}</p>
                  <p className="text-xs text-muted-foreground">{data.path.map((item) => item.name ?? item.type).join(" → ")}</p>
                </div>
              </Disclosure>
            </div>
          ) : null}
        </div>

      </DialogContent>
    </Dialog>
  );
}

function Disclosure({ children, onToggle, open, title }: {
  children: React.ReactNode;
  onToggle: () => void;
  open: boolean;
  title: string;
}) {
  return (
    <div>
      <button className="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground" onClick={onToggle} type="button">
        {title}
        <ChevronDown className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

function AccessLevelSelect({ onChange, value }: { onChange: (value: AccessLevel) => void; value: AccessLevel }) {
  const t = useT();
  return (
    <Select onValueChange={(nextValue) => onChange(nextValue as AccessLevel)} value={value}>
      <SelectTrigger aria-label={t("access.level")} className="w-28" size="sm"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="viewer">{t("access.view")}</SelectItem>
        <SelectItem value="commenter">{t("access.comment")}</SelectItem>
        <SelectItem value="editor">{t("common.edit")}</SelectItem>
        <SelectItem value="manager">{t("access.edit")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function PrincipalRow({ detail, iconType, name, trailing }: { detail: string; iconType: Principal["type"]; name: string; trailing: React.ReactNode }) {
  return (
    <div className="flex min-h-11 items-center gap-2.5 py-1.5">
      <PrincipalIcon type={iconType} value={name} />
      <div className="min-w-0 flex-1"><p className="truncate text-sm">{name}</p><p className="truncate text-xs text-muted-foreground">{detail}</p></div>
      {trailing}
    </div>
  );
}

function PrincipalIcon({ type, value }: { type: Principal["type"]; value: string }) {
  return (
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-medium uppercase text-muted-foreground">
      {type === "team" ? <UsersRound className="size-4" /> : value.trim().slice(0, 1) || <UserRound className="size-4" />}
    </span>
  );
}

function principalKey(principal: Principal) {
  return `${principal.type}:${principal.id}`;
}

function principalName(principal: Principal, data: ResourceAccess) {
  if (principal.type === "team") return data.teams.find((team) => team.id === principal.id)?.name ?? "Unknown team";
  const member = data.members.find((item) => item.userId === principal.id);
  return member?.displayName ?? member?.email ?? "Unknown member";
}

function principalDetail(principal: Principal, data: ResourceAccess) {
  if (principal.type === "team") return "Team";
  return data.members.find((item) => item.userId === principal.id)?.email ?? "Workspace member";
}
